import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, LogIn, FileText, PlayCircle, Sparkles, CheckCircle2, RefreshCw, XCircle, FolderOpen } from "lucide-react";
import { analysisApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const decMeta = {
  rozwijaj: { color: "#16a34a", icon: CheckCircle2, label: "ROZWIJAJ" },
  "zmień": { color: "#ca8a04", icon: RefreshCw, label: "ZMIEŃ" },
  "odrzuć": { color: "#dc2626", icon: XCircle, label: "ODRZUĆ" },
};
const entryLabel = (e) => (e === "firma" ? "Prowadzę firmę" : "Mam pomysł");
const statusMeta = (s) => (s === "analyzed"
  ? { label: "Gotowy raport", cls: "border-brand-accent/40 bg-brand-accent/10 text-brand-accent" }
  : { label: "Wywiad w toku", cls: "border-brand-line bg-brand-elevated text-brand-sec" });
const fmtDate = (v) => { try { return new Date(v).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" }); } catch { return v || "—"; } };

export default function BizAnalyses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (user === null) return;            // jeszcze ładujemy sesję
    if (user === false) { setItems([]); return; }
    analysisApi.myAnalyses()
      .then((d) => setItems(d.items || []))
      .catch(() => { setError(true); setItems([]); });
  }, [user]);

  const openSession = (it) => navigate(`/biznes/analiza/${it.entry === "firma" ? "firma" : "idea"}?sid=${it.id}`);

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      <header className="mx-auto flex max-w-content items-center justify-between px-4 py-5 md:px-8">
        <Link to="/biznes" className="inline-flex items-center gap-2 text-sm text-brand-sec hover:text-brand-text"><ArrowLeft size={16} /> Biznes i pomysły</Link>
        {user === false && <Link to="/logowanie" className="inline-flex items-center gap-1.5 text-sm text-brand-sec hover:text-brand-text"><LogIn size={15} /> Zaloguj się</Link>}
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-28 md:px-8" data-testid="biz-analyses-page">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-field bg-brand-accent/12 text-brand-accent"><FolderOpen size={22} /></div>
          <div>
            <h1 className="font-display text-2xl font-semibold sm:text-3xl" data-testid="biz-analyses-title">Moje analizy</h1>
            <p className="text-[14px] text-brand-sec">Twoje zapisane analizy biznesu. Otwórz gotowy raport lub dokończ wywiad.</p>
          </div>
        </div>

        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-accent" /></div>
        ) : user === false ? (
          <div className="rounded-panel border border-brand-accent/40 bg-brand-accent/10 p-6 text-center" data-testid="biz-analyses-login">
            <p className="text-sm text-brand-text">Zaloguj się, aby zobaczyć swoje zapisane analizy.</p>
            <Link to="/logowanie" className="mt-3 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on"><LogIn size={16} /> Zaloguj się</Link>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-panel border border-brand-line bg-brand-surface p-8 text-center" data-testid="biz-analyses-empty">
            <p className="font-display text-lg font-semibold">Nie masz jeszcze żadnych analiz</p>
            <p className="mt-1 text-[14px] text-brand-sec">{error ? "Nie udało się wczytać listy. Spróbuj odświeżyć stronę." : "Zacznij od krótkiego wywiadu — zapiszemy Twój projekt automatycznie."}</p>
            <Link to="/biznes/analiza/idea" data-testid="biz-analyses-start" className="mt-4 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on"><Sparkles size={16} /> Nowa analiza</Link>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="biz-analyses-list">
            {items.map((it) => {
              const isReady = it.status === "analyzed";
              const st = statusMeta(it.status);
              const dm = it.decision ? decMeta[it.decision] : null;
              return (
                <li key={it.id} data-testid={`biz-analysis-item-${it.id}`} className="flex flex-col gap-3 rounded-panel border border-brand-line bg-brand-surface p-4 transition-colors hover:border-brand-accent/50 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-display text-base font-semibold" data-testid={`biz-analysis-name-${it.id}`}>{it.name || "Analiza bez nazwy"}</p>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${st.cls}`} data-testid={`biz-analysis-status-${it.id}`}>{st.label}</span>
                      {dm && <span className="rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ color: dm.color, background: dm.color + "1a" }}>{dm.label}</span>}
                    </div>
                    <p className="mt-1 text-[12px] text-brand-muted">{entryLabel(it.entry)} · zaktualizowano {fmtDate(it.updated_at)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openSession(it)}
                    data-testid={`biz-analysis-open-${it.id}`}
                    className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-button px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5 ${isReady ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-text hover:border-brand-accent/60"}`}
                  >
                    {isReady ? <><FileText size={15} /> Otwórz raport</> : <><PlayCircle size={15} /> Kontynuuj</>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
